
import { MessageCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, isToday } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ListLoadingState } from "@/components/loading/ListLoadingState";

interface ConversationListProps {
  conversations: any[];
  onSelectConversation: (id: string) => void;
  currentUserId: string;
  isLoading?: boolean;
}

export const ConversationList = ({
  conversations,
  onSelectConversation,
  currentUserId,
  isLoading = false,
}: ConversationListProps) => {
  if (isLoading) {
    return <ListLoadingState />;
  }

  if (!conversations?.length) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center text-black p-4 text-center">
        <div>
          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
          <p>No messages yet</p>
          <p className="text-sm">Your conversations will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <ScrollArea className="flex-1">
        <div className="px-4 py-2 space-y-2">
          {conversations.map((conversation) => {
            const otherUser = conversation.cook_id === currentUserId
              ? conversation.foodie
              : conversation.cook;
            
            const lastMessage = conversation.last_message;
            const hasUnread = conversation.unread_count > 0;
            
            const messageDate = lastMessage ? new Date(lastMessage.created_at) : null;
            const timeString = messageDate ? (
              isToday(messageDate) 
                ? format(messageDate, "h:mm a")
                : format(messageDate, "MMM d")
            ) : "";

            const initials = otherUser?.first_name && otherUser?.last_name
              ? `${otherUser.first_name[0]}${otherUser.last_name[0]}`
              : "";

            return (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation.id)}
                className="w-full p-3 text-left hover:bg-accent/10 rounded-lg transition-colors"
              >
                <div className="flex items-start gap-3">
                  <Avatar className="h-12 w-12 flex-shrink-0">
                    {otherUser?.image_url ? (
                      <AvatarImage 
                        src={otherUser.image_url} 
                        alt={`${otherUser.first_name} ${otherUser.last_name}`} 
                      />
                    ) : (
                      <AvatarFallback className="uppercase">
                        {initials}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold truncate text-black ${hasUnread ? 'font-bold' : ''}`}>
                          {otherUser?.first_name} {otherUser?.last_name}
                        </p>
                        {hasUnread && (
                          <Badge variant="default" className="h-5 w-5 rounded-full p-0 flex items-center justify-center">
                            {conversation.unread_count}
                          </Badge>
                        )}
                      </div>
                      {timeString && (
                        <span className="text-xs text-black ml-2 flex-shrink-0">
                          {timeString}
                        </span>
                      )}
                    </div>
                    {conversation.meal && (
                      <p className="text-sm text-black truncate mb-1">
                        Re: {conversation.meal.name}
                      </p>
                    )}
                    {lastMessage && (
                      <p className={`text-sm truncate ${hasUnread ? 'text-black font-medium' : 'text-muted-foreground'}`}>
                        {lastMessage.content}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
